/* eslint-disable no-plusplus */
import { createContext, useCallback, useContext, useState, useMemo, useReducer, useRef } from "react";
import { useNotification } from "@hooks/useNotifications";
import { checkWithTimeout, arraysEqual } from "@libs/utils";
import { useAuth } from "features/auth";
import type { IItemTranslation } from "features/editor/types/item";
import type { PanelType } from "features/editor/types/panels";
import type { ITranslationUpdateParams, IValidationError } from "features/editor/types";
import type { ITranslationItemsResponse } from "features/workspaces/types";

interface ListState {
  items: IItemTranslation[];
  total: number;
}

type ListAction =
  | {
      type: "ADD_ITEMS";
      payload: ITranslationItemsResponse;
    }
  | {
      type: "RESET_ITEMS";
    }
  | {
      type: "SET_ITEM_ERRORS";
      payload: {
        index: number;
        errors: IValidationError[];
      };
    }
  | {
      type: "SET_ITEM_APPROVE";
      payload: {
        index: number;
        isApproved: boolean;
      };
    }
  | {
      type: "UPDATE_ITEM";
      payload: {
        index: number;
        message: string;
        author: string;
        isApproved: boolean;
      };
    };

const initialState: ListState = {
  items: [],
  total: 0,
};

interface IPanelContext {
  activePanel: PanelType;
  setActivePanel: (type: PanelType) => void;
}

type IEditorContext = {
  list: ListState;
  activeItemIndex: number | undefined;
  setActiveItemIndex: (index: number | undefined) => void;
  addListItems(items: ITranslationItemsResponse): void;
  resetListItems: () => void;
  setListItemErrors: (index: number, errors: IValidationError[]) => void;
  setListItemApprove: (index: number, isApproved: boolean) => void;
  setListItem: (index: number, message: string, isApproved: boolean) => void;
  goTo: ({ type, direction }: { type: "error" | "unapproved" | "next"; direction: "next" | "prev" }) => void;
  isTaskProcessing: boolean;
  onTaskProcessAbort: () => void;
  approvedCount: number;
  setApprovedCount: (count: number) => void;
  syncApprovedCount: (data: ITranslationUpdateParams) => void;
} & IPanelContext;

export const EditorContext = createContext<IEditorContext | null>(null);

function reducer(state: ListState, action: ListAction) {
  switch (action.type) {
    case "RESET_ITEMS":
      return {
        ...initialState,
      };

    case "ADD_ITEMS": {
      const prevLength = state.items.length;
      const items = [
        ...state.items,
        ...action.payload.list.map((item, index) => ({
          ...item,
          index: prevLength + index,
          errors: [],
          isApproved: item.translation.isApproved,
        })),
      ];

      return {
        items,
        total: action.payload.totalCount,
      };
    }

    case "SET_ITEM_ERRORS":
      if (arraysEqual(state.items[action.payload.index].errors, action.payload.errors)) {
        return state;
      }

      return {
        items: state.items.map(item =>
          item.index === action.payload.index ? { ...item, errors: action.payload.errors } : item,
        ),
        total: state.total,
      };

    case "SET_ITEM_APPROVE":
      return {
        items: state.items.map(item =>
          item.index === action.payload.index ? { ...item, isApproved: action.payload.isApproved } : item,
        ),
        total: state.total,
      };

    case "UPDATE_ITEM":
      return {
        items: state.items.map(item => {
          if (item.index === action.payload.index) {
            const result = { ...item };
            const isTextModified = result.translation.text !== action.payload.message;

            result.translation.text = action.payload.message;
            result.translation.isApproved = action.payload.isApproved;
            result.isApproved = action.payload.isApproved;

            if (isTextModified) {
              requestAnimationFrame(() => {
                // handle the event in the next event loop
                document.dispatchEvent(
                  new CustomEvent("editor:item:refresh", { detail: { translationId: result.translation.id } }),
                );
              });
            }

            return result;
          }

          return item;
        }),
        total: state.total,
      };

    default:
      throw new Error();
  }
}

const focusOnItem = (index: number, type?: "editor" | "item") => {
  const selector =
    type === "editor"
      ? `.list--editor > div:nth-child(${index + 1}) .editor__content`
      : `.list--editor > div:nth-child(${index + 1})`;
  const item = document.querySelector(selector);

  if (item) {
    item.scrollIntoView({ block: "center" });

    if (type === "editor") {
      window.getSelection()?.setPosition(item, item.childNodes.length || 0);
    }
  }
};

export const EditorProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const auth = useAuth();
  const { showInfo, showError } = useNotification();

  const [activeItemIndex, setActiveItemIndex] = useState<number | undefined>();
  const [activePanel, setActivePanel] = useState<PanelType>("History");
  const [list, dispatch] = useReducer(reducer, initialState);
  const [isTaskProcessing, setIsTaskProcessing] = useState(false);
  const listRef = useRef(list);
  listRef.current = list;

  const addListItems = (items: ITranslationItemsResponse) => {
    dispatch({ type: "ADD_ITEMS", payload: items });
  };

  const resetListItems = () => {
    setActiveItemIndex(undefined);
    dispatch({ type: "RESET_ITEMS" });
  };

  const setListItemErrors = (index: number, errors: IValidationError[]) => {
    dispatch({ type: "SET_ITEM_ERRORS", payload: { index, errors } });
  };

  const setListItemApprove = (index: number, isApproved: boolean) => {
    dispatch({ type: "SET_ITEM_APPROVE", payload: { index, isApproved } });
  };

  const setListItem = (index: number, message: string, isApproved: boolean) => {
    dispatch({ type: "UPDATE_ITEM", payload: { index, message, author: auth.user?.email, isApproved } });
  };

  const [abortController, setAbortController] = useState<AbortController | null>(null);

  const onTaskProcessAbort = useCallback(() => {
    if (abortController) {
      abortController.abort();
      setIsTaskProcessing(false); // Stop the task processing indicator
    }
  }, [abortController]);

  // Go to
  const goTo = useCallback(
    async ({
      type,
      direction,
      currentIndex,
    }: {
      type: "error" | "unapproved" | "next";
      direction: "next" | "prev";
      currentIndex?: number;
    }) => {
      let index = currentIndex ?? activeItemIndex ?? -1;
      let isPossible = true;

      const controller = new AbortController();
      setAbortController(controller);

      while (isPossible) {
        if (direction === "next") {
          index++;
        } else {
          index--;
        }

        if (index < 0 || index >= list.total) {
          isPossible = false;
          // TODO: investigate if we need to show a message here
          // showInfo("Can't find such item.");
          break;
        }

        let isCurrent = false;

        if (type === "next") {
          isCurrent = true;
        } else if (type === "error" && listRef.current.items[index]?.errors?.length) {
          isCurrent = true;
          setActivePanel("Validation");
        } else if (type === "unapproved" && !listRef.current.items[index]?.isApproved) {
          isCurrent = true;
        }

        if (isCurrent) {
          setActiveItemIndex(index);
          focusOnItem(index, type === "next" ? "editor" : "item");
          return;
        }

        if (listRef.current.items.length === index + 1) {
          const prevLength = listRef.current.items.length;
          focusOnItem(index, "item");

          setIsTaskProcessing(true);
          // Wait for fetching new items
          try {
            // Do not fetch if it is last item
            if (list.total !== listRef.current.items.length) {
              // eslint-disable-next-line no-await-in-loop
              await checkWithTimeout(
                300,
                10000,
                () => Boolean(listRef.current.items.length > prevLength),
                controller.signal,
              );
            }
          } catch (error) {
            if (controller.signal.aborted) {
              showInfo("Operation aborted.");
            } else {
              showError("Failed to get items. Please try again.");
            }
          }

          setIsTaskProcessing(false);
        }
      }
    },
    [activeItemIndex, list],
  );

  // Approved count
  const [approvedCount, setApprovedCount] = useState(0);
  const syncApprovedCount = (data: ITranslationUpdateParams) => {
    const prevStatus = list.items.find(item => item.original.id === data.originalId)?.isApproved;

    if (prevStatus === data.isApproved) {
      // Nothing changed
      return;
    }

    setApprovedCount(prevCount => (data.isApproved ? prevCount + 1 : prevCount - 1));
  };

  const value = useMemo(
    () => ({
      list,
      addListItems,
      resetListItems,
      setListItemErrors,
      setListItemApprove,
      setListItem,

      activeItemIndex,
      setActiveItemIndex,
      activePanel,
      setActivePanel,

      goTo,

      isTaskProcessing,
      onTaskProcessAbort,

      approvedCount,
      setApprovedCount,
      syncApprovedCount,
    }),
    [list, activeItemIndex, activePanel, isTaskProcessing, approvedCount],
  );

  return <EditorContext.Provider value={value}>{children}</EditorContext.Provider>;
};

export const useEditorContext = () => {
  const context = useContext(EditorContext);
  if (!context) {
    throw new Error("useEditorContext must be used within an EditorProvider");
  }
  return context;
};
